import { faLocationDot, faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTripContext } from '@/contexts/TripContext';
import { Button } from '@/design-system/primitives/Button';
import { ChipButton } from '@/design-system/primitives/ChipButton';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import { TextInput } from '@/design-system/primitives/TextInput';
import { type AppCategory, appCategories, buildUserPin } from '@/types/itinerary';
import { categoryDefinitions } from '@/utils/categories';

export interface CreateCustomPinDraft {
  latitude: number;
  longitude: number;
}

export interface CreateCustomPinModalProps {
  /** Coordinates where the user requested the pin. `null` keeps the
   *  modal closed. */
  draft: CreateCustomPinDraft | null;
  onClose: () => void;
}

// `other` is intentionally skipped — it's a fallback bucket that
// shouldn't be a user-facing choice. Everything else is fair game.
const userPinCategories = appCategories.filter((c): c is AppCategory => c !== 'other');

/**
 * Modal for creating a custom map pin and immediately adding it to the
 * active trip. Triggered by a right-click / long-press on the map.
 *
 * Provider-only fields (photos, hours, rating, etc.) are left blank —
 * the UI hides those panels for `source === 'user'` items.
 */
export const CreateCustomPinModal = ({ draft, onClose }: CreateCustomPinModalProps) => {
  const { selectedTripId, addPlace, navigatePanelToTrip } = useTripContext();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<AppCategory>('attractions');
  const [address, setAddress] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameInputId = useId();
  const addressInputId = useId();

  // Reset the form whenever the modal opens with a fresh coord — we
  // don't want yesterday's typed name re-appearing on the next open.
  useEffect(() => {
    if (!draft) return;
    setName('');
    setCategory('attractions');
    setAddress('');
    // Focus the name input after the dialog animation has a frame to
    // mount — focusing immediately races React's commit on first open.
    const handle = window.setTimeout(() => nameInputRef.current?.focus(), 50);
    return () => window.clearTimeout(handle);
  }, [draft]);

  // Body-scroll lock — same pattern as PhotoLightbox + CollaborateModal.
  useEffect(() => {
    if (!draft) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [draft]);

  if (!draft) return null;

  const canSubmit = name.trim().length > 0 && selectedTripId != null;

  const handleSubmit = () => {
    if (!(canSubmit && selectedTripId)) return;
    const item = buildUserPin({
      name,
      latitude: draft.latitude,
      longitude: draft.longitude,
      appCategory: category,
      address: address || undefined,
    });
    addPlace(selectedTripId, item, 'activity');
    navigatePanelToTrip(selectedTripId);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl-Enter or plain Enter from any non-textarea field submits.
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add a custom pin"
      className="fixed inset-0 z-modal flex items-center justify-center p-4 motion-safe:animate-kawaii-modal-scrim"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="absolute inset-0 bg-content-scrim" aria-hidden />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-content-border max-h-[85vh] flex flex-col motion-safe:animate-kawaii-modal-dialog"
      >
        <div className="flex items-start gap-2 p-4 border-b border-content-divider">
          <span
            className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-secondary-subtle text-secondary-emphasized"
            aria-hidden
          >
            <FontAwesomeIcon icon={faLocationDot} style={{ width: 14, height: 14 }} />
          </span>
          <div className="flex-1 min-w-0">
            <SectionEyebrow as="h3">Custom Pin</SectionEyebrow>
            <p className="text-xs text-content-subtle leading-snug mt-1">
              Drop a map pin here for a place the search didn't surface — a friend's house, a
              viewpoint, a campsite, etc.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full hover:bg-content-focal text-content-subtle hover:text-content-text flex items-center justify-center transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} style={{ width: 14, height: 14 }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label
              htmlFor={nameInputId}
              className="block text-xs font-medium text-content-subtle mb-1"
            >
              Name
            </label>
            <TextInput
              id={nameInputId}
              ref={nameInputRef}
              size="sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Boopily bungalow"
            />
          </div>

          <div>
            <span className="block text-xs font-medium text-content-subtle mb-1.5">Category</span>
            <div className="flex flex-wrap gap-1.5">
              {userPinCategories.map((cat) => {
                const def = categoryDefinitions[cat];
                return (
                  <ChipButton
                    key={cat}
                    icon={def.icon}
                    selected={category === cat}
                    onClick={() => setCategory(cat)}
                  >
                    {def.label}
                  </ChipButton>
                );
              })}
            </div>
          </div>

          <div>
            <label
              htmlFor={addressInputId}
              className="block text-xs font-medium text-content-subtle mb-1"
            >
              Address <span className="text-content-disabled font-normal">(optional)</span>
            </label>
            <TextInput
              id={addressInputId}
              size="sm"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="123 Sweet Treat Rd, Sweetsville"
            />
          </div>

          <p className="text-[11px] text-content-disabled leading-snug">
            Coordinates: {draft.latitude.toFixed(5)}, {draft.longitude.toFixed(5)}
          </p>
        </div>

        <div className="shrink-0 flex items-center gap-2 p-4 border-t border-content-divider">
          <Button size="sm" variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit} className="flex-1">
            Add to trip
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
